import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, Checkbox, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import { splitPassage } from './passageFormat';

// Styled Components
const ReviewHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    /* MUI zeroes DialogContent's top padding when it follows a DialogTitle
       (.MuiDialogTitle-root + .MuiDialogContent-root), so the breathing room
       above this row has to come from here. */
    padding: 20px 0 14px;
`;

const ReviewCount = styled.div`
    font-size: 15px;
    font-weight: 650;
    color: ${({ theme }) => theme.text};
    font-variant-numeric: tabular-nums;
`;

const ReviewHint = styled.div`
    font-size: 12px;
    color: ${({ theme }) => theme.textMuted};
`;

// Sits at the end of the list, so it is the last thing read before Save. The
// wording is the original notice that used to appear on the generation loader.
const Disclaimer = styled.div`
    margin-top: 18px;
    padding: 14px 0 4px;
    border-top: 1px solid ${({ theme }) => theme.border};
`;

const DisclaimerText = styled.p`
    margin: 0;
    font-size: 11px;
    line-height: 1.5;
    color: ${({ theme }) => theme.textMuted};

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: 700;
    }
`;

// Each question is its own card. Collapsed cards keep the list scannable, and
// AccordionDetails unmounts while closed, which is what replaces the old
// react-window virtualisation.
const QuestionCard = styled(Accordion)`
    && {
        margin: 0 0 10px;
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: ${({ theme }) => theme.rMd};
    }
`;

const SummaryRow = styled.div`
    display: grid;
    grid-template-columns: 26px minmax(0, 1fr);
    align-items: start;
    gap: 12px;
    width: 100%;
`;

const IndexBadge = styled.span`
    margin-top: 1px;
    width: 26px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: ${({ theme }) => theme.surface3};
    color: ${({ theme }) => theme.textMuted};
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
`;

const SummaryText = styled.div`
    min-width: 0;
`;

const SummaryQuestion = styled.div`
    font-size: 13.5px;
    line-height: 1.4;
    color: ${({ theme }) => theme.text};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const SummaryAnswer = styled.div`
    margin-top: 3px;
    font-size: 12px;
    color: ${({ theme }) => theme.textMuted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    strong {
        color: ${({ theme }) => theme.success};
        font-weight: 650;
    }
`;

const FieldStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const GroupLabel = styled.div`
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textSubtle};
`;

// The option field used to sit inside a FormControlLabel's `label`, which does
// not stretch - that is why long options were clipped mid-sentence. A grid row
// lets the field take every pixel that is left.
const OptionRow = styled.div`
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr);
    align-items: center;
    gap: 4px;
`;

const MetaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
`;

const DetailBody = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 20px;
`;

const ContentSection = styled.div`
    flex: 1;
    min-width: 260px;
`;

const StyledImage = styled.img`
    width: 200px;
    height: 200px;
    border-radius: 8px;
    object-fit: cover;
`;

export default function Questionsdialog({ open, handleClose, questions, onSave, testId, passage,caseStudyOption,mediaUrl,questionType,assessmentLevel ,directAttachmentType, readOnly = false}) {
    const [editedContent, setEditedContent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openIndexes, setOpenIndexes] = useState(() => new Set([0]));

//console.log(passage)

    useEffect(() => {
    if (questions && questions.length > 0) {
        const updatedQuestions = questions.map((q) => ({
            ...q,
            questionType: questionType || '',
            assessmentLevel: Array.isArray(assessmentLevel)
                ? assessmentLevel.join(',')
                : assessmentLevel || '',
        }));
        setEditedContent(updatedQuestions);
    }
}, [questions]);

    const handleContentChange = (e, index, field) => {
        const updatedContent = [...editedContent];
        updatedContent[index][field] = e.target.value;
        setEditedContent(updatedContent);
    };

    const handleOptionChange = (e, index, optionIndex) => {
        const updatedContent = [...editedContent];
        updatedContent[index].answer[optionIndex].answer = e.target.value;
        setEditedContent(updatedContent);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(editedContent, passage,caseStudyOption,mediaUrl );
        } catch (error) {
            console.error('Save Error:', error);
        } finally {
            setSaving(false);
            handleClose();
        }
    };

    async function getImageInFromDalle3(prompt, index, setLoading) {
        setLoading(true);
        try {
            const response = await dalle3Api(prompt);
            if (response.ok) {
                const responseData = await response.json();
                const imageUrl = responseData?.data?.[0]?.url || '';
                const updatedContent = [...editedContent];
                updatedContent[index].asset = imageUrl;
                setEditedContent(updatedContent);
            } else {
                console.error('Error: Unexpected response:', response.status, response.statusText);
                throw new Error('Unexpected response');
            }
        } catch (error) {
            console.error('Error:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function dalle3Api(prompt) {
        const apiUrl1 = process.env.REACT_APP_API_URL1;
        const token = localStorage.getItem('token');
        const response = await fetch(apiUrl1 + '/api/generate_image', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
                'Accept-Encoding': 'gzip, deflate, br',
                Accept: '/',
                Connection: 'keep-alive',
          "X-Api-Version": 'v2'
            },
            body: JSON.stringify({
                model: 'dall-e-3',
                prompt: prompt,
                n: 1,
                size: '1024x1024',
                test_id: testId,
            }),
        });

        return response;
    }

    useEffect(() => {
        editedContent.forEach((question, index) => {
            if (question.prompt && !question.asset) {
                getImageInFromDalle3(question.prompt, index, setLoading);
            }
        });
    }, [editedContent]);

    const isOpen = (index) => openIndexes.has(index);

    const toggleQuestion = (index) => {
        setOpenIndexes((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index); else next.add(index);
            return next;
        });
    };

    /// The correct option's text, shown on the collapsed card so the list can be
    /// reviewed without opening every question.
    const correctAnswerOf = (question) => {
        const match = (question.answer || []).find((o) => o.is_correct === 1);
        return match ? match.answer : '';
    };

    const renderQuestion = (question, index) => {
        const correct = correctAnswerOf(question);
        return (
            <QuestionCard
                key={index}
                expanded={isOpen(index)}
                onChange={() => toggleQuestion(index)}
                disableGutters
                elevation={0}
                // Collapsed cards drop their fields from the DOM. This is what
                // makes it safe to render the whole set without react-window:
                // a 50-question batch would otherwise mount ~500 MUI inputs.
                TransitionProps={{ unmountOnExit: true }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <SummaryRow>
                        <IndexBadge>{index + 1}</IndexBadge>
                        <SummaryText>
                            <SummaryQuestion>
                                {question.question || 'Untitled question'}
                            </SummaryQuestion>
                            {correct && (
                                <SummaryAnswer>
                                    Answer: <strong>{correct}</strong>
                                </SummaryAnswer>
                            )}
                        </SummaryText>
                    </SummaryRow>
                </AccordionSummary>

                <AccordionDetails>
                    <DetailBody>
                        <ContentSection>
                            <FieldStack>
                                <TextField
                                    value={question.question || ''}
                                    onChange={(e) => handleContentChange(e, index, 'question')}
                                    variant="outlined"
                                    InputProps={{ readOnly }}
                                    fullWidth
                                    multiline
                                    label={`Question ${index + 1}`}
                                />

                                <FieldGroup>
                                    <GroupLabel>Answer options</GroupLabel>
                                    {question.answer.map((option, optionIndex) => (
                                        <OptionRow key={optionIndex}>
                                            <Checkbox
                                                checked={option.is_correct === 1}
                                                disabled={readOnly}
                                                inputProps={{ 'aria-label': `Option ${optionIndex + 1} is the correct answer` }}
                                            />
                                            <TextField
                                                value={option.answer || ''}
                                                onChange={(e) => handleOptionChange(e, index, optionIndex)}
                                                variant="outlined"
                                                InputProps={{ readOnly }}
                                                fullWidth
                                                multiline
                                                label={`Option ${optionIndex + 1}`}
                                            />
                                        </OptionRow>
                                    ))}
                                </FieldGroup>

                                <MetaGrid>
                                    <TextField
                                        value={question.questionType || questionType || ''}
                                        onChange={(e) => handleContentChange(e, index, 'questionType')}
                                        variant="outlined"
                                        fullWidth
                                        label="Question Type"
                                    />
                                    <TextField
                                        value={question.assessmentLevel || assessmentLevel || ''}
                                        onChange={(e) => handleContentChange(e, index, 'assessmentLevel')}
                                        variant="outlined"
                                        fullWidth
                                        label="Assessment Level"
                                    />
                                </MetaGrid>

                                <TextField
                                    value={question.keywords || ''}
                                    onChange={(e) => handleContentChange(e, index, 'keywords')}
                                    variant="outlined"
                                    InputProps={{ readOnly }}
                                    fullWidth
                                    multiline
                                    label="Keywords"
                                />
                                <TextField
                                    value={question.hint || ''}
                                    onChange={(e) => handleContentChange(e, index, 'hint')}
                                    variant="outlined"
                                    InputProps={{ readOnly }}
                                    fullWidth
                                    multiline
                                    label="Hint"
                                />
                                <TextField
                                    value={question.explanation || ''}
                                    onChange={(e) => handleContentChange(e, index, 'explanation')}
                                    variant="outlined"
                                    InputProps={{ readOnly }}
                                    fullWidth
                                    multiline
                                    label="Explanation"
                                />
                            </FieldStack>
                        </ContentSection>
                        {loading ? (
                            <CircularProgress />
                        ) : question.asset && (
                            <StyledImage
                                src={question.asset}
                                alt={`Question ${index + 1}`}
                            />
                        )}
                    </DetailBody>
                </AccordionDetails>
            </QuestionCard>
        );
    };

    return (
       <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
    <DialogTitle>Generated Questions</DialogTitle>
    <DialogContent>
        {passage && (
            <div
                style={{
                    padding: '16px',
                    backgroundColor: 'oklch(0.985 0.004 260)',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid oklch(0.915 0.006 260)',
                    fontSize: '16px',
                    lineHeight: '1.6',
                }}
            >
                  <Typography variant="h6" gutterBottom>
                    {caseStudyOption === 'generateImage' ? ('Image') : ('Passage')}
                </Typography>
                {caseStudyOption === 'generateImage' ? (
                    <img
                        src={`data:image/png;base64,${passage}`}
                        alt="Generated Visual Passage"
                        style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px' }}
                    />
                ) : (
                    /* Title in bold, set apart from the body — same as every other
                       screen that renders a passage. */
                    (() => {
                        const { title, body } = splitPassage(passage);
                        return (
                            <>
                                {title && (
                                    <div style={{ fontWeight: 700, marginBottom: '12px' }}>{title}</div>
                                )}
                                <div style={{ whiteSpace: 'pre-wrap' }}>{body}</div>
                            </>
                        );
                    })()
                )}
            </div>
        )}
  {mediaUrl && directAttachmentType === 'image' ? (
    <img
        src={mediaUrl}
        alt="Generated Visual Passage"
        style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px' }}
    />
) : directAttachmentType === 'pdf' ? (
    <div style={{ whiteSpace: 'pre-wrap' }}>{passage}</div>
) : null}

        <ReviewHeader>
            <ReviewCount>
                {editedContent.length} {editedContent.length === 1 ? 'question' : 'questions'}
            </ReviewCount>
            <ReviewHint>
                {readOnly ? 'Select a question to see its answer and explanation.'
                          : 'Select a question to edit it, then save.'}
            </ReviewHint>
        </ReviewHeader>
        {editedContent.map(renderQuestion)}
        <Disclaimer>
            <DisclaimerText>
                <strong>Disclaimer:</strong> StudyAK is an AI-powered content generation tool provided for educational support.
                While it assists in creating assessments aligned with specific domains and subdomains, it is your responsibility
                to review and verify the accuracy, appropriateness, and relevance of all generated content before use.
                StudyAK Inc. makes no guarantees regarding the suitability of AI-generated questions for specific instructional or evaluative purposes.
            </DisclaimerText>
        </Disclaimer>
    </DialogContent>
    <DialogActions>
        {/* Outlined + contained gives the pair a clear primary action, instead
            of two identical text buttons. Both variants come from the shared
            MUI theme, so they match every other dialog in the portal. */}
        <Button onClick={handleClose} variant="outlined" color="primary">
            {readOnly ? 'Back' : 'Close'}
        </Button>
        {!readOnly && (
          <Button onClick={handleSave} variant="contained" color="primary">
              {saving ? <ButtonSpinnerSlot size={24} /> : 'Save'}
          </Button>
        )}
    </DialogActions>
</Dialog>

    );
}
